import {useEffect, useState, useMemo, useCallback} from 'react'
import {useParams, Link} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {ROUTES} from '../../../../../_gori/constants'
import {useVerifyHash} from '../../hooks'
import {isValidGoriSignatureHash} from '../../utils/validation'
import {clearLabelSessionStorage} from '../../utils/sessionStorageUtils'
import {useFraudDetection} from '../../hooks/useFraudDetection'
import {
  validateStorageData,
  isValidDataUrl,
  isValidOcrData,
  normalizeStatus,
} from '../../utils/verifyUtils'
import type {ManifestStatus} from '../../utils/verifyUtils'
import {LabelPreview} from './LabelPreview'
import {LoadingState} from './LoadingState'
import {StatusAlert} from './StatusAlert'

export function GoriSignatureVerify() {
  const {hash} = useParams<{hash: string}>()
  const intl = useIntl()

  useEffect(() => {
    if (!hash || !isValidGoriSignatureHash(hash)) {
      setErrorStatus('invalid')
    }
  }, [hash])

  useEffect(() => {
    // Cleanup on mount to clear any stale data
    if (hash && isValidGoriSignatureHash(hash)) {
      clearLabelSessionStorage(hash)
    }

    return () => {
      if (hash && isValidGoriSignatureHash(hash)) {
        clearLabelSessionStorage(hash)
      }
    }
  }, [hash])

  // Also add cleanup on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && hash && isValidGoriSignatureHash(hash)) {
        clearLabelSessionStorage(hash)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [hash])

  const {data, error, isLoading} = useVerifyHash(hash)
  const [errorStatus, setErrorStatus] = useState<'expired' | 'invalid' | null>(null)
  const [activeSlide, setActiveSlide] = useState(0)
  const [scrollNode, setScrollNode] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!scrollNode) return
    const handleScroll = () => {
      const slideWidth =
        (scrollNode.firstElementChild as HTMLElement)?.offsetWidth || scrollNode.offsetWidth
      setActiveSlide(Math.round(scrollNode.scrollLeft / slideWidth))
    }
    scrollNode.addEventListener('scroll', handleScroll, {passive: true})
    return () => scrollNode.removeEventListener('scroll', handleScroll)
  }, [scrollNode])

  const uploadedImage = useMemo(() => {
    if (!hash || !isValidGoriSignatureHash(hash)) return null
    const stored = sessionStorage.getItem(`label_image_${hash}`)
    const validated = validateStorageData(stored)
    return validated && isValidDataUrl(validated) ? validated : null
  }, [hash])

  const ocrAddresses = useMemo(() => {
    if (!hash || !isValidGoriSignatureHash(hash)) return null
    const stored = sessionStorage.getItem(`label_ocr_${hash}`)
    if (!stored) return null

    try {
      const parsed = JSON.parse(stored)
      return isValidOcrData(parsed) ? parsed : null
    } catch {
      return null
    }
  }, [hash])

  const ocrRegions = useMemo(() => {
    if (!hash || !isValidGoriSignatureHash(hash)) return null
    const stored = sessionStorage.getItem(`label_ocr_${hash}`)
    if (!stored) return null

    try {
      const parsed = JSON.parse(stored)
      return parsed?.regions || null
    } catch {
      return null
    }
  }, [hash])

  const [expectedLabelLoaded, setExpectedLabelLoaded] = useState(false)

  useEffect(() => {
    const img = new Image()
    img.src = '/media/gori/gori-signature/label.png'
    img.onload = () => setExpectedLabelLoaded(true)
    img.onerror = () => setExpectedLabelLoaded(true)
  }, [])

  useEffect(() => {
    const cleanupData = () => {
      if (hash && isValidGoriSignatureHash(hash)) {
        clearLabelSessionStorage(hash)
      }
    }

    return cleanupData
  }, [hash])

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hash && isValidGoriSignatureHash(hash)) {
        clearLabelSessionStorage(hash)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hash])

  const isManualEntry = !uploadedImage

  const {hasAddressMismatch, hasLabelBarcodeMismatch, fieldStatus} = useFraudDetection(
    data,
    ocrAddresses,
    isManualEntry
  )

  const status: ManifestStatus =
    errorStatus ||
    (hasAddressMismatch || hasLabelBarcodeMismatch
      ? 'fraud'
      : data?.valid
      ? normalizeStatus(data.manifest_status)
      : 'invalid')
  const showSingleLabel = isManualEntry || status === 'expired' || status === 'invalid'

  useEffect(() => {
    if (isLoading) return

    if (error) {
      setErrorStatus('invalid')
    } else if (!data) {
      setErrorStatus('invalid')
    } else if (data.valid === false) {
      const errorMessage = data.error || ''
      if (errorMessage.includes('expired') || errorMessage.includes('no longer be used')) {
        setErrorStatus('expired')
      } else {
        setErrorStatus('invalid')
      }
    } else if (!data.tracking_number) {
      setErrorStatus('invalid')
    }
  }, [data, error, isLoading])

  const addresses = useMemo(() => {
    if (!data || !data.valid) return {from: 'N/A', to: 'N/A', tracking: 'N/A'}

    const from = [
      [data.from_street1, data.from_street2].filter(Boolean).join(' '),
      [data.from_city, data.from_state, data.from_zip].filter(Boolean).join(' '),
    ].join('\n')

    const to = [
      [data.to_street1, data.to_street2].filter(Boolean).join(' '),
      [data.to_city, data.to_state, data.to_zip].filter(Boolean).join(' '),
    ].join('\n')

    const tracking =
      data.tracking_number?.match(/.{1,4}/g)?.join(' ') || data.tracking_number || 'N/A'

    return {fromZip: data.from_zip, from, to, tracking}
  }, [data])

  const handleIndicatorClick = useCallback(
    (index: number) => {
      setActiveSlide(index)
      if (scrollNode) {
        const slideWidth =
          (scrollNode.firstElementChild as HTMLElement)?.offsetWidth || scrollNode.offsetWidth
        scrollNode.scrollTo({
          left: slideWidth * index,
          behavior: 'smooth',
        })
      }
    },
    [scrollNode]
  )

  if (isLoading || !expectedLabelLoaded) {
    return <LoadingState />
  }

  return (
    <div className='gori-signature-results' role='main' aria-live='polite'>
      <div className='gori-signature-header-wrapper text-center'>
        <img
          src='/media/logos/gori_signature_logo.png'
          alt={intl.formatMessage({id: 'GORI_SIGNATURE_TITLE'})}
          className='gori-signature-logo mb-5'
        />
        {showSingleLabel && status !== 'invalid' ? (
          <p className='text-dark'>
            {intl.formatMessage(
              {id: 'GORI_SIGNATURE_RESULTS_MESSAGE'},
              {
                newLine: <br />,
              }
            )}
          </p>
        ) : (
          <p className='text-dark'>
            {intl.formatMessage({id: 'GORI_SIGNATURE_RESULTS_MESSAGE_AUTHENTICATED'})}
          </p>
        )}
        {isManualEntry && status !== 'fraud' && status !== 'invalid' && (
          <p className='text-dark'>
            {intl.formatMessage({id: 'GORI_SIGNATURE_MESSAGE_MANUAL'})}{' '}
            <Link
              to={ROUTES.GORI_SIGNATURE.METHOD_SELECT}
              state={{hash}}
              className='gori-signature-manifest-link fw-bold'
            >
              {intl.formatMessage({id: 'GORI_SIGNATURE_MESSAGE_MANUAL_LINK'})}
            </Link>
          </p>
        )}
      </div>

      <div className='gori-signature-content-wrapper'>
        {status !== 'invalid' && !(isManualEntry && status === 'expired') && (
          <>
            {/* Mobile: two separate cards that scroll/snap */}
            {!showSingleLabel && (
              <div className='gori-signature-mobile-slides' ref={setScrollNode}>
                <div className='gori-signature-comparison-card'>
                  <LabelPreview
                    image={uploadedImage || ''}
                    alt={intl.formatMessage({id: 'GORI_SIGNATURE_YOUR_LABEL'})}
                    showAddresses={false}
                    status={status}
                    addresses={addresses}
                    fieldStatus={fieldStatus}
                    regions={ocrRegions}
                  />
                </div>
                <div className='gori-signature-comparison-card'>
                  <LabelPreview
                    image='/media/gori/gori-signature/label.png'
                    alt={intl.formatMessage({id: 'GORI_SIGNATURE_EXPECTED_LABEL'})}
                    showAddresses={true}
                    status={status}
                    addresses={addresses}
                    fieldStatus={fieldStatus}
                  />
                </div>
              </div>
            )}

            {/* Tablet+: single card with two columns */}
            <div
              className={`gori-signature-comparison-card gori-signature-tablet-card ${
                !showSingleLabel ? 'gori-signature-tablet-card--dual' : ''
              }`}
            >
              <div className='row g-4 justify-content-center'>
                {!showSingleLabel && (
                  <div className='col-6'>
                    <LabelPreview
                      image={uploadedImage || ''}
                      alt={intl.formatMessage({id: 'GORI_SIGNATURE_YOUR_LABEL'})}
                      showAddresses={false}
                      status={status}
                      addresses={addresses}
                      fieldStatus={fieldStatus}
                      regions={ocrRegions}
                    />
                  </div>
                )}
                {showSingleLabel ? (
                  status === 'expired' ? (
                    <div className='col-12'>
                      <LabelPreview
                        image={uploadedImage || ''}
                        alt={intl.formatMessage({id: 'GORI_SIGNATURE_YOUR_LABEL'})}
                        showAddresses={false}
                        status={status}
                        addresses={addresses}
                        fieldStatus={fieldStatus}
                        regions={ocrRegions}
                      />
                    </div>
                  ) : (
                    <div className='col-12'>
                      <LabelPreview
                        image='/media/gori/gori-signature/label.png'
                        alt={intl.formatMessage({id: 'GORI_SIGNATURE_EXPECTED_LABEL'})}
                        showAddresses={true}
                        status={status}
                        addresses={addresses}
                        fieldStatus={fieldStatus}
                      />
                    </div>
                  )
                ) : (
                  <div className='col-6'>
                    <LabelPreview
                      image='/media/gori/gori-signature/label.png'
                      alt={intl.formatMessage({id: 'GORI_SIGNATURE_EXPECTED_LABEL'})}
                      showAddresses={true}
                      status={status}
                      addresses={addresses}
                      fieldStatus={fieldStatus}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {!showSingleLabel && (
          <div className='gori-signature-scroll-indicators mb-5'>
            <div
              className={activeSlide === 0 ? 'indicator active' : 'indicator'}
              onClick={() => handleIndicatorClick(0)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleIndicatorClick(0)
                }
              }}
              aria-label='View your label'
            ></div>
            <div
              className={activeSlide === 1 ? 'indicator active' : 'indicator'}
              onClick={() => handleIndicatorClick(1)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleIndicatorClick(1)
                }
              }}
              aria-label='View expected label'
            ></div>
          </div>
        )}

        <StatusAlert status={status} isManual={isManualEntry} />
      </div>
    </div>
  )
}
